/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Page Account Info Types
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Pavel Antonov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.11.0
 */

import type { IMultiBalance } from '@/main/pages/PageDashboard/components/Widgets/WidgetMultiBalances/widgetMultiBalancesTypes';

import type {
  IAccountDeliveryAddressInfo,
  IEdmDeliveryInfo,
  IPostDeliveryInfo,
} from './pageAccountInfoGeneralTypes';

// при загрузке данных для каждой вкладки нужно передать clientId для запроса API
export interface IAccountInfoStartPayload {
  clientId: string;
}

export interface IUpdateEmailInquiry {
  inquiryId: number | null;
}

export interface IAccountInfoFinancesGeneralMetaResponse {
  campaignsCount: number;
  multiBalances: IMultiBalance[];
  parameters: {
    isShowCampaigns: boolean;
    isShowReportButton: boolean;
    isStableCashBack: boolean;
  };
  unpaidBills: {
    amount: number;
    nearestPaymentDate: string;
    paid: number;
    quantity: number;
    unpaidAmount: number;
    unpaidBillsAllowed: boolean;
  };

  balance: {
    balance: number;
    totalBalance: number;
    financeAccums: number;
    virtualPayments: number;
    creditLimit: number;
    refreshDate: string;
  };
  initialBalance: {
    value: null | number;
    date: null | string;
  };
  subscribersCharges: {
    value: null | number;
    date: null | string;
  };
  accountPayments: {
    value: null | number;
    date: null | string;
  };
  creditLimit: number;
  loyaltyUser: {
    bonusBalance: null | number;
    level: null;
    program: null;
    status: {
      loyaltyProgramStatusId: null | number;
      name: string;
    };
    statusDate: null | string;
    urlHowToGetCashback: null | string;
  };
  isPromisedPaymentButtonAvailable: boolean;
  promisedPayments: IPromisedPayment[];
}

export interface IPromisedPayment {
  promisedPaymentId: number;
  amount: number;
  endDate: null | string;
}

export interface IAccountInfoMultiBalanceListResponse {
  multiBalances: IMultiBalance[];
}

export interface IAccountInfoFinancesDiscountsResponse {
  discounts: IDiscount[];
}

export interface IMultiBalanceEvent {
  amount: number;
  balanceActionTypeName: string;
  balanceDate: string;
  eventTypeName: string;
  msisdn: number;
  productName: string;
}

export interface IAccountInfoMultiBalanceEventsResponse {
  multiBalanceEvents: IMultiBalanceEvent[];
}

export interface IAccountInfoDeliveryData {
  address: IAccountDeliveryAddressInfo;
  airId: string;
  contactPerson: string;
  email: string;
  note: string;
  phone: string;
  useCourier: boolean;
  useEdm: boolean;
  useEmail: boolean;
  usePost: boolean;
}

export interface IAccountDeliveryInfoData {
  deliveryCountryId: number;
  deliveryData: IAccountInfoDeliveryData;
  edm?: IEdmDeliveryInfo;
  isAgreementEditingWarningShown: boolean;
  post?: IPostDeliveryInfo;
}
export interface IDiscount {
  discountId: string;
  endDate: string;
  headerName: string;
  infinite: boolean;
  measure: string;
  measureSpend: string;
  measureTotal: string;
  name: string;
  spend: number;
  total: number;
  unlimited: boolean;
  volume: number;
}

export interface IDeliveryEmail {
  email: string;
  inquiryId: number;
}

export enum EPageAccountInfo {
  GENERAL = 'general',
  FINANCIAL = 'financial',
  FINANCIAL_GENERAL = 'financial/general',
  FINANCIAL_DISCOUNTS = 'financial/discounts',
  FINANCIAL_MULTI_BALANCES = 'financial/multi-balances',
  SHIPPING_ACCS = 'shipping-accs',
}
